import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthProvider';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from '../api/useNotifications';
import type { InAppNotificationDto, NotificationType } from '../types/notification';

export function NotificationBell() {
  const auth = useAuth();
  const navigate = useNavigate();
  const query = useNotifications(Boolean(auth.activeCustomerId));
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  if (!auth.activeCustomerId) return null;

  const notifications = query.data?.notifications ?? [];
  const unreadCount = query.data?.unread_count ?? 0;

  function openNotification(item: InAppNotificationDto) {
    if (!item.read_at) markRead.mutate(item.notification_id);
    navigate(`/machines/${encodeURIComponent(item.machine_id)}/investigations/${encodeURIComponent(item.investigation_id)}`);
  }

  return (
    <div className="dropdown r42-notification-dropdown">
      <button
        className="r42-icon-button r42-notification-button"
        data-bs-toggle="dropdown"
        aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-expanded="false"
      >
        <i className="bi bi-bell" aria-hidden="true" />
        {unreadCount ? <span className="r42-notification-badge" aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span> : null}
      </button>
      <div className="dropdown-menu dropdown-menu-end r42-notification-menu">
        <div className="r42-notification-menu__head">
          <div>
            <strong>Notifications</strong>
            <span>{unreadCount ? `${unreadCount} unread` : 'You’re all caught up'}</span>
          </div>
          {unreadCount ? (
            <button type="button" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending}>
              Mark all read
            </button>
          ) : null}
        </div>
        <div className="r42-notification-list">
          {query.isPending ? (
            <div className="r42-notification-empty">Checking for updates…</div>
          ) : query.isError ? (
            <div className="r42-notification-empty">Notifications are temporarily unavailable.</div>
          ) : notifications.length === 0 ? (
            <div className="r42-notification-empty">No Investigation notifications yet.</div>
          ) : notifications.map((item) => (
            <button
              type="button"
              className={`r42-notification-item${item.read_at ? '' : ' is-unread'}`}
              key={item.notification_id}
              onClick={() => openNotification(item)}
            >
              <span className="r42-notification-item__icon"><i className={`bi ${iconFor(item.notification_type)}`} aria-hidden="true" /></span>
              <span className="r42-notification-item__copy">
                <span className="r42-notification-item__row">
                  <strong>{item.title}</strong>
                  <time dateTime={item.created_at}>{formatNotificationTime(item.created_at)}</time>
                </span>
                <span>{item.message}</span>
              </span>
              {!item.read_at ? <span className="r42-notification-unread-dot" aria-label="Unread" /> : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function iconFor(type: NotificationType): string {
  if (type === 'INVESTIGATION_CLOSED') return 'bi-check2-circle';
  if (type === 'INVESTIGATION_REOPENED') return 'bi-arrow-clockwise';
  return 'bi-search';
}

function formatNotificationTime(value: string, now = new Date()): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const age = Math.max(0, now.getTime() - date.getTime());
  if (age < 24 * 60 * 60 * 1000) {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  if (age < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
